import { CalendarTodayTwoTone, ContentCopyTwoTone } from '@mui/icons-material';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import ArticleTwoToneIcon from '@mui/icons-material/ArticleTwoTone';
import { alpha, Box, Button, Card, Divider, IconButton, LinearProgress, Stack, styled, Tooltip, Typography, useTheme } from '@mui/material';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { formatDate, formatDistance, formatPrice, getMaterials } from 'src/utils/FormatHelpers';
import InfiniteScroll from 'react-infinite-scroll-component';
import { AiMailConversationResource, WizardCalcMergeResource, WizardCalcPartResource } from 'src/backend/market';
import { DateTime } from 'luxon';
import { wT } from 'src/utils/wizardTranslations';
import { updatePaging } from 'src/redux/thunks/calculations.thunk';
import { ANALYZING } from 'src/statics/statics';
import CalcOverviewGridEmail from './CalcOverviewGridEmail';
import CalculationStatus from '../display/CalculationStatus/CalculationStatus';
import { LinearProgressWithLabel } from '../display/LinearProgressWithLabel';
import SurfLoader from '../display/Loader/SurfLoader';
import ColorDrops from '../display/ColorDrops';
import store from 'src/redux/store';

const GridBox = styled(Box)(
    ({ theme }) => `
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
        grid-gap: ${theme.spacing(4)};
        padding: ${theme.spacing(2)};

        .MuiCard-root.disabled {
            cursor: not-allowed;
            opacity: 0.7;
        }
        .MuiCard-root:not(.disabled):hover {
            box-shadow: 0 0 0 2px ${theme.colors.info.main}, 0 0 12px 1px ${theme.colors.info.main};
        }
        .MuiCard-root:not(.disabled)active {
            box-shadow: 0 0 0 2px ${theme.colors.info.main}, 0 0 18px 3px ${theme.colors.info.main};
        }

        .calculation-status {
            font-size: 14px;
            line-height: 1.47;
            gap: 4px;
            padding: 3px 5px;
            border-radius: 5px;
        }
        .calculation-status.unopened {
            padding-left: 10px;
            padding-right: 10px;
        }
        .calculation-status .MuiSvgIcon-root {
            width: 0.8em;
            height: 0.8em;
        }

        ${theme.breakpoints.down('sm')} {
            padding: ${theme.spacing(1)};
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(4)};
            overflow: hidden;
        }
    `
);

interface Props {
    calculations: Array<WizardCalcMergeResource>;
    mailConversations: { [uniqueId: string]: AiMailConversationResource };
    isLoading: boolean;
    paging: { page: number; pageSize: number; total: number; rowCount: number };
    handleEditClicked: Function;
    handleViewClicked: Function;
    handleDuplicateClicked: Function;
    handleDeleteClicked: Function;
}

const CalcOverviewGrid: React.FC<Props> = ({ calculations, mailConversations, paging, isLoading, handleEditClicked, handleViewClicked, handleDuplicateClicked, handleDeleteClicked }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const theme = useTheme();
    const s = store.getState();

    const handleLoadNext = () => {
        dispatch(updatePaging({ pageSize: paging.pageSize + 10 }));
    };

    const getRequester = (row: WizardCalcMergeResource) => {
        if (row.requester && row.requester !== 'NOT_SET') return row.requester;
        if (row.company) return row.company;
        if (row.name) return row.name;
        return t('unspecifiedCustomer');
    };

    const getDeliveryPercentage = (row: WizardCalcMergeResource) => {
        const hoursSinceDeliveryDate = row.deliveryDate ? DateTime.fromISO(row.deliveryDate).diffNow().as('hours') : 0;
        const hoursBeforeWishDate = row.wishDate ? DateTime.fromISO(row.wishDate).diffNow().as('hours') : 0;
        const sumHours = Math.abs(hoursSinceDeliveryDate) + Math.abs(hoursBeforeWishDate);

        return hoursSinceDeliveryDate > 0 ? 0 : hoursBeforeWishDate <= 0 ? 100 : (100 * Math.abs(hoursSinceDeliveryDate)) / sumHours;
    };

    useEffect(() => {
        if (paging.pageSize !== 15) {
            dispatch(updatePaging({ pageSize: 15 }));
        }
    }, []);

    const getParts = (parts: Array<WizardCalcPartResource>): Array<WizardCalcPartResource> => {
        if (parts.length <= 4) return parts;

        return parts.reduce((accumulator, part, index) => {
            if (index < 3) {
                accumulator[index] = part;
            } else if (index === 3) {
                accumulator[3] = { partCount: 1, itemNameKey: 'rest', price: part.price };
            } else {
                accumulator[3].partCount++;
                accumulator[3].price += part.price;
            }
            return accumulator;
        }, []);
    };

    const getFileExtensions = (row: WizardCalcMergeResource) => {
        const fileExtensions = [];
        row.parts.forEach((part) => {
            const fileName = part.dataSourceOriginalFileName || '';
            if (fileName.split('.').length > 1) {
                fileExtensions.push(fileName.split('.').pop().toUpperCase());
            }
        });
        return [...new Set(fileExtensions)];
    };

    const rows = _.values(calculations);
    return (
        <Box pb={8} m={{ md: -2 }} maxWidth={{ xs: '100%', md: 'unset' }} height="100%">
            {rows.length === 0 && isLoading ? (
                <Stack height="100%" justifyContent="center" alignItems="center" pt={8}>
                    <SurfLoader />
                </Stack>
            ) : rows.length === 0 ? (
                <Stack alignItems="center">
                    <Typography my={2}>{t('noCalculations')}</Typography>
                </Stack>
            ) : (
                <InfiniteScroll
                    next={handleLoadNext}
                    hasMore={rows.length !== paging.total || isLoading}
                    endMessage={
                        rows.length > 20 && (
                            <Typography my={2} textAlign="center">
                                {t('noMoreCalculations')}
                            </Typography>
                        )
                    }
                    loader={<LinearProgress variant="indeterminate" />}
                    dataLength={rows.length}
                >
                    <GridBox maxWidth="100%">
                        {rows.map((row: WizardCalcMergeResource) => (
                            <Card
                                key={row.id}
                                sx={{
                                    p: 2,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    position: 'relative'
                                }}
                                className={row.status === ANALYZING ? 'disabled' : ''}
                                onClick={() => handleEditClicked(row)}
                            >
                                {row.wizardId && (
                                    <Stack sx={{ position: 'absolute', top: 0, right: 0 }}>
                                        <Typography sx={{ bgcolor: theme.colors.primary.light, color: 'white', px: 0.8, py: 0.2, fontSize: 10, fontWeight: 'bold', borderBottomLeftRadius: 8 }}>
                                            WEB
                                        </Typography>
                                    </Stack>
                                )}
                                <Stack flexGrow={1} maxWidth="100%" overflow="hidden">
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography fontSize={16} color="text.secondary">
                                            #{row.uniqueId}
                                        </Typography>
                                        <Stack direction="row" alignItems="center">
                                            <CalendarTodayTwoTone sx={{ fontSize: '1rem', mr: 0.6 }} />
                                            <Typography variant="subtitle2">{formatDate(row.createdAt)}</Typography>
                                        </Stack>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between" mt={1}>
                                        <Stack direction="row" alignItems="center" maxWidth="100%" overflow="hidden">
                                            <Typography variant="h4" noWrap>
                                                {getRequester(row)}
                                            </Typography>
                                            {row.summaryPersonDataType === WizardCalcMergeResource.summaryPersonDataType.REGULAR_CUSTOMER && (
                                                <Typography
                                                    noWrap
                                                    ml={0.6}
                                                    fontSize="0.85em"
                                                    fontWeight="bold"
                                                    lineHeight={1}
                                                    bgcolor={theme.colors.warning.lighter}
                                                    color={theme.colors.warning.main}
                                                    p={0.4}
                                                    borderRadius={0.6}
                                                    className="regular-customer"
                                                >
                                                    {t('regularCustomer')}
                                                </Typography>
                                            )}
                                        </Stack>
                                        {!!row.distance && (
                                            <Typography variant="subtitle2" color="primary" whiteSpace="nowrap">
                                                {formatDistance(row.distance)}
                                            </Typography>
                                        )}
                                    </Stack>
                                    <Divider sx={{ my: 2 }} />
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle2" noWrap color="text.secondary">
                                            {t('status')}
                                        </Typography>
                                        <CalculationStatus status={row.status} importedWizardId={row.wizardId} showUnopened={true} mailConversation={mailConversations?.[row.uniqueId]} />
                                    </Stack>
                                    <CalcOverviewGridEmail mailConversation={mailConversations?.[row.uniqueId]} calcId={row.uniqueId} />
                                    <Stack direction="row" gap={4} justifyContent="space-between" mt={2}>
                                        <Stack maxWidth="100%" overflow="hidden">
                                            <Typography variant="subtitle2" lineHeight={1.2} noWrap color="text.secondary">
                                                {t('anlieferung')}
                                            </Typography>
                                            <Typography fontSize={15} lineHeight={1.4} noWrap fontWeight="bold">
                                                {formatDate(row.deliveryDate, 'dd.MM.yyyy') || '-'}
                                            </Typography>
                                        </Stack>

                                        {row.deliveryDate && row.wishDate && (
                                            <Stack justifyContent="flex-end" maxWidth="100%" overflow="hidden">
                                                <Typography fontSize={15} lineHeight={1.4} noWrap color="text.secondary">
                                                    ⇠ {row.throughputDays || ''} {t(row.throughputDays === 0 ? 'sameDay' : row.throughputDays > 1 ? 'days' : 'day')} ⇢
                                                </Typography>
                                            </Stack>
                                        )}

                                        <Stack textAlign="right" maxWidth="100%" overflow="hidden">
                                            <Typography variant="subtitle2" lineHeight={1.2} noWrap color="text.secondary">
                                                {t('abholung')}
                                            </Typography>
                                            <Typography fontSize={15} lineHeight={1.4} noWrap fontWeight="bold">
                                                {formatDate(row.wishDate, 'dd.MM.yyyy') || '-'}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <LinearProgressWithLabel value={getDeliveryPercentage(row)} />
                                    <Divider sx={{ my: 2 }} />

                                    <Stack direction="row" maxWidth="100%" overflow="hidden">
                                        <Stack mr={3} overflow="hidden">
                                            <Typography variant="subtitle2">{t('articles')}</Typography>
                                            {getParts(row.parts).map((part, index) =>
                                                part.itemNameKey === 'rest' ? (
                                                    <Typography key={index} variant="subtitle2" fontWeight="bold" fontSize={13} noWrap>
                                                        {t('xMoreParts', { x: part.partCount })}
                                                    </Typography>
                                                ) : (
                                                    <Typography key={index} variant="subtitle2" fontWeight="bold" fontSize={13} noWrap>
                                                        {part.partCount} × {part.userDefinedPartName || wT(part.itemNameKey, s)}
                                                    </Typography>
                                                )
                                            )}
                                        </Stack>
                                        <Stack ml="auto" textAlign="right" maxWidth="100%">
                                            <Typography variant="subtitle2">{t('price')}</Typography>
                                            {getParts(row.parts).map((part, index) => (
                                                <Typography key={index} variant="subtitle2" fontWeight="bold" fontSize={13} color="primary" noWrap>
                                                    {formatPrice(part.price)}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Stack>
                                    <Typography textAlign="right" variant="h3" fontWeight="bold" color="primary" mt={1} noWrap>
                                        {formatPrice(row.price)}
                                    </Typography>
                                    <Divider sx={{ my: 2 }} />
                                    <Stack mb={2}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="subtitle2" noWrap color="text.secondary">
                                                {t('colors')}
                                            </Typography>
                                            <Typography variant="subtitle2" fontSize={15} fontWeight="bold">
                                                {row.colors?.length > 0 ? <ColorDrops colors={row.colors} /> : '-'}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="subtitle2" noWrap color="text.secondary">
                                                {t('materials')}
                                            </Typography>
                                            <Tooltip
                                                title={
                                                    getMaterials(row.materials)
                                                        .map((material) => wT(material, s))
                                                        .join(', ') || '-'
                                                }
                                            >
                                                <Typography variant="subtitle2" fontSize={15} fontWeight="bold" noWrap>
                                                    {getMaterials(row.materials)
                                                        .map((material) => wT(material, s))
                                                        .join(', ') || '-'}
                                                </Typography>
                                            </Tooltip>
                                        </Stack>
                                        {getFileExtensions(row).length > 0 && (
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography variant="subtitle2" noWrap color="text.secondary">
                                                    {t('fileExtensions')}
                                                </Typography>
                                                <Tooltip title={getFileExtensions(row).join(', ')}>
                                                    <Typography variant="subtitle2" fontSize={15} fontWeight="bold" noWrap>
                                                        {getFileExtensions(row).join(', ')}
                                                    </Typography>
                                                </Tooltip>
                                            </Stack>
                                        )}
                                    </Stack>

                                    <Stack direction="row" spacing={1} mt="auto">
                                        <Button
                                            variant="contained"
                                            sx={{ flexGrow: 1 }}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleEditClicked(row);
                                            }}
                                            disabled={row.status === ANALYZING}
                                        >
                                            {t('edit')}
                                        </Button>
                                        <Tooltip title={t('viewSummary')}>
                                            <div>
                                                <IconButton
                                                    color="primary"
                                                    sx={{
                                                        bgcolor: theme.colors.primary.lighter,
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.colors.primary.main, 0.25)
                                                        }
                                                    }}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleViewClicked(row);
                                                    }}
                                                    disabled={row.status === ANALYZING}
                                                >
                                                    <ArticleTwoToneIcon />
                                                </IconButton>
                                            </div>
                                        </Tooltip>
                                        <Tooltip title={t('kalulationDuplizieren')}>
                                            <div>
                                                <IconButton
                                                    color="inherit"
                                                    sx={{
                                                        bgcolor: theme.colors.alpha.black[10],
                                                        '&:hover': {
                                                            bgcolor: theme.colors.alpha.black[20]
                                                        }
                                                    }}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleDuplicateClicked(row);
                                                    }}
                                                    disabled={row.status === ANALYZING}
                                                >
                                                    <ContentCopyTwoTone />
                                                </IconButton>
                                            </div>
                                        </Tooltip>
                                        <Tooltip title={t('kalulationLschen')}>
                                            <div>
                                                <IconButton
                                                    color="error"
                                                    sx={{
                                                        bgcolor: theme.colors.error.lighter,
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.colors.error.main, 0.2)
                                                        }
                                                    }}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleDeleteClicked(row.uniqueId);
                                                    }}
                                                    disabled={row.status === ANALYZING}
                                                >
                                                    <DeleteTwoToneIcon />
                                                </IconButton>
                                            </div>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            </Card>
                        ))}
                    </GridBox>
                </InfiniteScroll>
            )}
        </Box>
    );
};

export default CalcOverviewGrid;
